// Maps a gameId → its game-specific UI plug-ins for the shared frame:
//   HoleInputs  — per-hole live betting inputs on the scorecard
//   ResultsView — final settlement view
//   rolesFor(game, hole) → { [playerId]: badge } — wolf/partner/shuck badges
//     shown on each player's score card.
// Kept in a .jsx module so the pure registry stays node-testable.
import GWolfHoleInputs from './GWolfHoleInputs.jsx'
import GWolfResults from './GWolfResults.jsx'
import GStandingsResults from './GStandingsResults.jsx'
import GBbbHoleInputs from './GBbbHoleInputs.jsx'
import GSnakeHoleInputs from './GSnakeHoleInputs.jsx'
import GCthHoleInputs from './GCthHoleInputs.jsx'
import { wolfIndexFor, shuckIndexFor } from './ggPlayWolf.js'

function wolfRoles(game, hole) {
  const players = game?.players || []
  const n = players.length
  if (!n) return {}
  const opts = game.meta?.options || {}
  const isBls = opts.variant === 'blShuck'
  const wolfIdx = wolfIndexFor(hole, n, isBls)
  const shuckIdx = shuckIndexFor(hole, n, isBls)
  const wolf = players[wolfIdx]
  const shuck = players[shuckIdx]
  const p = game.wolf?.[hole] || (isBls ? { loneWolf: false, partnerId: null, shuckPartnerId: shuck.id } : { loneWolf: true, partnerId: null })
  const roles = {}
  const lone = p.loneWolf && !p.partnerId && !p.shuckPartnerId
  roles[wolf.id] = lone ? '🐺 LONE' : '🐺'
  if (p.partnerId) roles[p.partnerId] = '🤝'
  if (isBls) {
    if (p.shuckPartnerId === shuck.id) { if (!roles[shuck.id]) roles[shuck.id] = '🤝 SHUCK' }
    else if (shuck.id !== wolf.id) roles[shuck.id] = '❌ SHUCKED'
  }
  return roles
}

// Card display order — mirrors the app: cards roll up each hole so the current
// Wolf is on top, then (wolfIdx + k) % n cyclically (forward standard, backward
// BLS via wolfIndexFor). Returns the players[] in display order.
function wolfCardOrder(game, hole) {
  const players = game?.players || []
  const n = players.length
  if (!n) return players
  const isBls = game.meta?.options?.variant === 'blShuck'
  const wolfIdx = wolfIndexFor(hole, n, isBls)
  return Array.from({ length: n }, (_, k) => players[(wolfIdx + k) % n])
}

const GAME_UI = {
  wolf: { HoleInputs: GWolfHoleInputs, ResultsView: GWolfResults, rolesFor: wolfRoles, cardOrder: wolfCardOrder },
  // Point/holder games: plain score entry (no per-hole taps) + generic standings board.
  nines: { ResultsView: GStandingsResults },
  chicago: { ResultsView: GStandingsResults },
  rabbit: { ResultsView: GStandingsResults },
  matchplay: { ResultsView: GStandingsResults },
  dogfight: { ResultsView: GStandingsResults },
  'stroke-play': { ResultsView: GStandingsResults },
  // Team games (2 teams of 2): plain score entry + team standings board.
  vegas: { ResultsView: GStandingsResults },
  scotch: { ResultsView: GStandingsResults },
  'four-ball': { ResultsView: GStandingsResults },
  dots: { ResultsView: GStandingsResults },
  // Event games — per-hole tap panels.
  bbb: { HoleInputs: GBbbHoleInputs, ResultsView: GStandingsResults },
  snake: { HoleInputs: GSnakeHoleInputs, ResultsView: GStandingsResults },
  nassau: { ResultsView: GStandingsResults },
  sixes: { ResultsView: GStandingsResults },
  shamble: { ResultsView: GStandingsResults },
  shuffle: { ResultsView: GStandingsResults },
  'team-cup': { HoleInputs: GCthHoleInputs, ResultsView: GStandingsResults },
}

export const getGameUI = (key) => GAME_UI[key] || {}
